import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Check } from 'lucide-react';
import axios from 'axios';
import { useBranding } from '@/context/BrandingContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PORTAL_CONFIG = {
  admin:   { subtitle: 'Sign in to your Admin panel',   showSignup: false, showClientSignup: false, forgotHref: '/forgot-password?portal=admin' },
  partner: { subtitle: 'Sign in to your Partner panel', showSignup: true,  showClientSignup: false, forgotHref: '/forgot-password?portal=partner' },
  client:  { subtitle: 'Sign in to your Client panel',  showSignup: false, showClientSignup: true,  forgotHref: '/forgot-password?portal=client' },
};

const FEATURE_CARDS = [
  { icon: '💰', title: '60% Avg Margins', color: '#f59e0b' },
  { icon: '⚡', title: 'Instant Activation', color: '#06b6d4' },
  { icon: '🏷️', title: 'Set Your Own Markup', color: '#8b5cf6' },
  { icon: '🌐', title: 'Custom Subdomain', color: '#10b981' },
  { icon: '🤝', title: 'Multi-level Network',color: '#3b82f6' },
  { icon: '📊', title: 'Full Dashboard', color: '#ef4444' },
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { branding, isCustomDomain } = useBranding();

  const portalType = location.pathname.startsWith('/admin') ? 'admin'
    : location.pathname.startsWith('/partner') ? 'partner'
    : 'client';   // /login and /client/login both show client portal
  const config = PORTAL_CONFIG[portalType];
  const isPartnerPortal = portalType === 'partner';

  const [loginOffer, setLoginOffer] = useState(null);

  useEffect(() => {
    if (!isPartnerPortal) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/active-partner-offer`)
      .then(res => {
        const d = res.data;
        const showOnLogin = d.id && (d.pages || []).some(p => p === 'login' || p === 'all');
        setLoginOffer(showOnLogin ? d : null);
      })
      .catch(() => setLoginOffer(null));
  }, [isPartnerPortal]);

  // Read ?next= param for post-login redirect (used by white label pages)
  const nextUrl = (() => {
    const params = new URLSearchParams(location.search);
    const n = params.get('next');
    return n && n.startsWith('/') ? n : null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password, portalType);
      toast.success('Login successful!');
      if (nextUrl && result.role === 'client') {
        navigate(nextUrl);
        return;
      }
      switch (result.role) {
        case 'admin':
        case 'level_3_support':
        case 'level_2_support':
        case 'level_1_support':
          navigate('/admin/dashboard');
          break;
        case 'reseller':
        case 'sub_reseller':
          if (result.kyc_status !== 'approved') {
            navigate('/partner/kyc-portal');
          } else {
            navigate('/reseller/dashboard');
          }
          break;
        case 'client':
          navigate('/client/dashboard');
          break;
        default:
          navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const offerBullets = loginOffer?.bullets || [
    loginOffer?.wallet_credit_amount > 0 && `₹${Number(loginOffer.wallet_credit_amount).toLocaleString('en-IN')} wallet credit on signup`,
    'No hidden charges',
    'Instant account activation',
    '24/7 dedicated support',
  ].filter(Boolean);

  // ── Admin / Client portals: simple centered layout (unchanged) ──
  if (!isPartnerPortal) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          background: 'linear-gradient(145deg, #f0f4f8 0%, #d9e2ec 50%, #e4ecf4 100%)',
        }}
      >
        <div
          className="w-full max-w-md bg-white rounded-2xl p-8 shadow-[0_8px_48px_rgba(15,30,61,0.18),0_0_0_1px_rgba(255,255,255,0.9)]"
          style={{ position: 'relative', zIndex: 1 }}
          data-testid="login-card"
        >
          <FormCard
            config={config}
            email={email} setEmail={setEmail}
            password={password} setPassword={setPassword}
            showPassword={showPassword} setShowPassword={setShowPassword}
            loading={loading}
            handleSubmit={handleSubmit}
            branding={branding}
            isCustomDomain={isCustomDomain}
          />
        </div>
      </div>
    );
  }

  // ── Partner portal: ALWAYS split layout (left panel + right form) ──
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: '#fff' }}
    >

      <div
        className="w-full flex rounded-2xl overflow-hidden shadow-[0_8px_48px_rgba(15,30,61,0.18),0_0_0_1px_rgba(255,255,255,0.9)]"
        style={{ position: 'relative', zIndex: 1, maxWidth: 860 }}
        data-testid="login-card"
      >
        {/* ── LEFT PANEL: offer if active, else default branding ── */}
        {/* ── LEFT PANEL: always show branding + offer details below if active ── */}
        <div
          className="flex flex-col p-8 offer-panel overflow-y-auto"
          style={{
            background: loginOffer?.bg_color || 'linear-gradient(145deg, #1a3a8f 0%, #0f2260 100%)',
            color: loginOffer?.text_color || '#fff',
            minWidth: 0,
            flex: '0 0 42%',
          }}
        >
          {/* ── DEFAULT / PARTNER BRANDING ── */}
          {isCustomDomain && branding.logo_url ? (
            <img
              src={branding.logo_url.startsWith('/') ? `${BACKEND_URL}${branding.logo_url}` : branding.logo_url}
              alt={branding.company_name || 'Logo'}
              className="h-10 object-contain self-start mb-5"
            />
          ) : (
            <span
              className="inline-block self-start px-3 py-1 rounded-full text-xs font-bold mb-5"
              style={{ background: '#FF6B35', color: '#fff' }}
            >
              ✦ Partner Program
            </span>
          )}

          <p className="font-extrabold leading-snug mb-2" style={{ fontSize: 'clamp(20px, 2.5vw, 26px)' }}>
            {isCustomDomain && branding.company_name
              ? `Welcome to ${branding.company_name}`
              : 'Welcome to CloudSetu Partner Portal'}
          </p>
          <p className="text-sm mb-1" style={{ opacity: 0.75 }}>
            Start your recurring business now.
          </p>
          <p className="text-sm mb-5" style={{ opacity: 0.75 }}>
            Build your white-label hosting business and grow your network.
          </p>

          <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)', // 3 equal columns
                gap: 8,
                marginBottom: 16,
              }}
            >
            {FEATURE_CARDS.map((f, i) => (
              <div
                key={i}
                style={{
                  padding: '5px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: f.color + '33',
                    border: `1px solid ${f.color}66`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    marginBottom: 2,
                  }}
                >
                  {f.icon}
                </span>
                <p style={{ fontSize: 11, fontWeight: 700, opacity: 0.95, lineHeight: 1.2 }}>{f.title}</p>
                <p style={{ fontSize: 10, opacity: 0.6, lineHeight: 1.3 }}>{f.subtitle}</p>
              </div>
            ))}
          </div>

          {/* ── ACTIVE OFFER DETAILS (shown below branding when offer exists) ── */}
          {loginOffer && (
            <div
              className="rounded-xl p-4 mt-2"
              style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)' }}
            >
              {/* Offer badge */}
              {loginOffer.badge_text && (
                <span
                  className="inline-block px-2 py-0.5 rounded-full text-xs font-bold mb-3"
                  style={{ background: loginOffer.badge_color || '#FF6B35', color: '#fff' }}
                >
                  {loginOffer.badge_text}
                </span>
              )}

              {/* Wallet credit amount */}
              {loginOffer.wallet_credit_amount > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium mb-0.5" style={{ opacity: 0.75 }}>🎁 You'll receive</p>
                  <p
                    className="font-extrabold leading-none"
                    style={{ fontSize: 'clamp(28px, 4vw, 38px)', color: loginOffer.badge_color || '#FF6B35' }}
                  >
                    ₹{Number(loginOffer.wallet_credit_amount).toLocaleString('en-IN')}
                  </p>
                  <p className="text-xs font-medium mt-0.5" style={{ opacity: 0.75 }}>in Free Wallet Credits</p>
                </div>
              )}

              {/* Offer title if no wallet amount */}
              {!loginOffer.wallet_credit_amount && loginOffer.title && (
                <p className="font-bold mb-2" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>
                  {loginOffer.title}
                </p>
              )}

              {/* Offer bullets */}
              {offerBullets.length > 0 && (
                <ul className="space-y-1.5 mb-3">
                  {offerBullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs" style={{ opacity: 0.9 }}>
                      <span
                        className="mt-0.5 flex-shrink-0 rounded-full flex items-center justify-center"
                        style={{ width: 15, height: 15, background: 'rgba(255,255,255,0.2)' }}
                      >
                        <Check size={9} strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}

              {/* Offer description / T&C */}
              <p className="text-xs" style={{ opacity: 0.45 }}>
                {loginOffer.description || 'Limited time offer. T&C apply.'}
              </p>
            </div>
          )}

          {/* Footer */}
          <p className="text-xs mt-auto pt-6" style={{ opacity: 0.4 }}>
            Trusted by partners across India. No hidden charges.
          </p>
        </div>

        {/* ── RIGHT: Form panel (unchanged) ── */}
        <div className="flex-1 bg-white p-8 flex flex-col justify-center min-w-0">
          <FormCard
            config={config}
            email={email} setEmail={setEmail}
            password={password} setPassword={setPassword}
            showPassword={showPassword} setShowPassword={setShowPassword}
            loading={loading}
            handleSubmit={handleSubmit}
            branding={branding}
            isCustomDomain={isCustomDomain}
          />
        </div>
      </div>

      {/* Mobile: stack vertically */}
      <style>{`
        @media (max-width: 640px) {
          .offer-panel {
            flex: none !important;
            width: 100% !important;
          }
          [data-testid="login-card"] {
            flex-direction: column !important;
          }
        }
      `}</style>
    </div>
  );
}

// ── Shared form component ──
function FormCard({
  config, email, setEmail, password, setPassword,
  showPassword, setShowPassword, loading, handleSubmit,
  branding, isCustomDomain,
}) {
  const logoUrl = isCustomDomain && branding?.logo_url
    ? (branding.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : branding.logo_url)
    : '/cloudsetu-logo.png';
  const logoAlt = (isCustomDomain && branding?.company_name) ? branding.company_name : 'CloudSetu';

  return (
    <>
      <div className="flex justify-center mb-6">
        <img src={logoUrl} alt={logoAlt} className="h-12 object-contain" />
      </div>

      <div className="text-center mb-6">
        <h1 className="text-2xl font-semibold text-slate-800 mb-1" data-testid="login-title">
          Welcome Back
        </h1>
        <p className="text-slate-500 text-sm">{config.subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-700 text-sm font-medium">Email</Label>
          <Input
            id="email" type="email" placeholder="your@email.com"
            value={email} onChange={e => setEmail(e.target.value)} required
            className="h-12 bg-slate-50 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-blue-500 text-slate-700 placeholder:text-slate-400"
            data-testid="login-email-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-slate-700 text-sm font-medium">Password</Label>
          <div className="relative">
            <Input
              id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
              value={password} onChange={e => setPassword(e.target.value)} required
              className="h-12 bg-slate-50 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-blue-500 text-slate-700 placeholder:text-slate-400 pr-12"
              data-testid="login-password-input"
            />
            <button
              type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              tabIndex={-1} data-testid="login-password-toggle"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full h-12 text-white font-medium text-base rounded-lg mt-2"
          disabled={loading}
          style={{ background: 'linear-gradient(90deg, #2563eb 0%, #06b6d4 100%)', border: 'none' }}
          data-testid="login-submit-button"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>

        <div className="text-center">
          <a href={config.forgotHref} className="text-sm text-slate-500 hover:text-blue-600 transition-colors" data-testid="forgot-password-link">
            Forgot password?
          </a>
        </div>
      </form>

      {config.showSignup && (
        <p className="text-sm text-center text-slate-500 mt-5">
          Don't have an account?{' '}
          <a href="/partner/signup" className="text-cyan-600 hover:text-cyan-700 font-medium" data-testid="signup-link">
            Partner SignUp
          </a>
        </p>
      )}
      {config.showClientSignup && (
        <p className="text-sm text-center text-slate-500 mt-5">
          Don't have an account?{' '}
          <a href="/signup" className="text-cyan-600 hover:text-cyan-700 font-medium" data-testid="client-signup-link">
            Sign Up
          </a>
        </p>
      )}
    </>
  );
}